
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Calendar, Brain, Globe, Users, TrendingUp } from 'lucide-react';

export default function CurrentAffairsPrelaunch() {
  const handleJoinWaitlist = () => {
    const waitlistUrl = import.meta.env.VITE_WAITLIST_URL || "https://example.com/waitlist";
    window.open(waitlistUrl, '_blank');
  };

  const handleStayUpdated = () => {
    const updateUrl = import.meta.env.VITE_UPDATE_URL || "mailto:contact@edaptica.com";
    window.open(updateUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8 sm:py-12">
        <div className="text-center max-w-4xl mx-auto mb-12">
          {/* Logo and Title */}
          <div className="flex justify-center mb-6">
            <img 
              src="/lovable-uploads/a4dbcce3-4ec8-4832-93cb-49db994c040c.png" 
              alt="Affairnaut Logo" 
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full shadow-lg"
            />
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Edaptica Current Affairs ✨
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-4">
            Powered by <span className="font-semibold text-purple-600">Affairnaut</span>
          </p>
          
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-800 mb-6">
            India's First AI-Powered LMS for Current Affairs
          </h2>
          
          <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 text-sm sm:text-base mb-8">
            🚀 Launching on 1st September
          </Badge>
          
          <p className="text-lg text-gray-700 mb-8 leading-relaxed max-w-2xl mx-auto">
            Stay ahead of the curve with smarter, faster, and adaptive current affairs learning. 
            Affairnaut brings you a revolutionary AI-powered experience designed to make current 
            affairs engaging, interactive, and exam-ready.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={handleJoinWaitlist}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              size="lg"
            >
              👉 Join the Waitlist
            </Button>
            
            <Button 
              onClick={handleStayUpdated}
              variant="outline"
              className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 px-8 py-3 text-lg font-semibold transition-all duration-300"
              size="lg"
            >
              👉 Stay Updated
            </Button>
          </div>
        </div>
        
        {/* Image Gallery */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto">
          <div className="flex justify-center">
            <img 
              src="/lovable-uploads/108b4d40-d0e0-4c78-bca0-f03919475888.png" 
              alt="Affairnaut Astronaut with Books" 
              className="w-64 h-64 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow"
            />
          </div>
          <div className="flex justify-center">
            <img 
              src="/lovable-uploads/11ec155f-fd70-4f93-b1e2-14ea4b099322.png" 
              alt="Astronaut with News" 
              className="w-64 h-64 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow"
            />
          </div>
          <div className="flex justify-center">
            <img 
              src="/lovable-uploads/453dd190-7b67-46a7-afd4-5c8782066fa8.png" 
              alt="Indian Astronaut" 
              className="w-64 h-64 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow"
            />
          </div>
          <div className="flex justify-center">
            <img 
              src="/lovable-uploads/0e6e1c56-b34d-484d-a615-150c94208d75.png" 
              alt="Astronaut with Indian Flag and News" 
              className="w-64 h-64 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow"
            />
          </div>
          <div className="flex justify-center">
            <img 
              src="/lovable-uploads/67f3adb1-265b-42af-899e-c1893b489601.png" 
              alt="Astronaut with Globe and Books" 
              className="w-64 h-64 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow"
            />
          </div>
        </div>
        
        {/* Why Affairnaut Section */}
        <Card className="max-w-4xl mx-auto bg-white/60 backdrop-blur-sm border-0 shadow-xl">
          <CardContent className="p-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8 text-gray-800">
              Why Affairnaut?
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-lg">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">📚 AI-Powered Learning</h4>
                  <p className="text-gray-600">Personalized, adaptive content tailored to your strengths.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-green-500 to-teal-600 p-3 rounded-lg">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">📰 Daily Current Affairs</h4>
                  <p className="text-gray-600">Delivered in an engaging, quiz-ready format.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">🧠 Smart Retention</h4>
                  <p className="text-gray-600">Flashcards, mock tests, and analytics to help you retain more in less time.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-lg">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">🌏 Made for Everyone</h4>
                  <p className="text-gray-600">Covers competitive exams, interviews, and real-world awareness.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Final CTA Section */}
        <div className="text-center mt-12 max-w-2xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-800">
            Be the First to Experience It
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            We're opening the doors on 1st September. Don't miss out on India's next big leap in current affairs learning.
          </p>
          
          <Button 
            onClick={handleJoinWaitlist}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-10 py-4 text-xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            size="lg"
          >
            👉 Join the Waitlist
          </Button>
        </div>
      </div>
    </div>
  );
}
